import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { getClassifieds, submitClassified } from "../lib/api";
import ImageUpload from "../components/ImageUpload";
import { Modal, Field, inputCls } from "../admin/AdminLayout";
import { Tag, Plus, Phone } from "lucide-react";

const CATS = ["All", "For Sale", "Real Estate", "Autos", "Jobs", "Services", "Garage Sales", "Pets"];
const empty = { category: "For Sale", title: "", description: "", price: "", image: "", name: "", contact: "" };

export default function Classifieds() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("All");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = (c) => getClassifieds(c).then(setItems).catch(() => {});
  useEffect(() => { load(filter); }, [filter]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Please add a title.");
    setBusy(true);
    try {
      const res = await submitClassified(form);
      toast.success(res.message);
      setOpen(false); setForm(empty); load(filter);
    } catch { toast.error("Could not post your ad."); }
    finally { setBusy(false); }
  };

  return (
    <div className="paper-texture min-h-screen">
      <div className="border-b border-[var(--paper-border)] bg-[var(--navy)]">
        <div className="mx-auto max-w-7xl px-4 py-10">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="eyebrow flex items-center gap-2 text-[var(--gold)]"><Tag size={14} /> Self-Serve Marketplace</p>
              <h1 className="font-display text-4xl font-extrabold text-white sm:text-5xl">Classifieds</h1>
              <p className="font-serif-body mt-2 max-w-xl text-white/80">Buy, sell and hire locally. Place your own ad with a photo in minutes.</p>
            </div>
            <button onClick={() => { setForm(empty); setOpen(true); }} data-testid="place-ad-btn" className="font-ui flex items-center gap-2 rounded-full bg-[var(--gold)] px-5 py-3 text-xs font-semibold uppercase tracking-wider text-white hover:bg-[#b08f48]">
              <Plus size={16} /> Place an Ad
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-8">
        <div className="mb-8 flex flex-wrap gap-2">
          {CATS.map((c) => (
            <button key={c} onClick={() => setFilter(c)} data-testid={`classified-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              className={`font-ui rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-wider ${filter === c ? "border-[var(--navy)] bg-[var(--navy)] text-white" : "border-[var(--paper-border)] bg-white text-slate-600 hover:border-[var(--navy)]"}`}>
              {c}
            </button>
          ))}
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((c) => (
            <div key={c.id} data-testid={`classified-${c.id}`} className="card-hover flex flex-col overflow-hidden rounded-lg border border-[var(--paper-border)] bg-white">
              {c.image && <div className="img-zoom h-44 overflow-hidden"><img src={c.image} alt={c.title} className="h-full w-full object-cover" /></div>}
              <div className="flex flex-1 flex-col p-4">
                <div className="flex items-center justify-between">
                  <span className="eyebrow text-[var(--gold)]">{c.category}</span>
                  {c.price && <span className="font-display text-lg font-bold text-[var(--navy)]">{c.price}</span>}
                </div>
                <h3 className="font-display mt-1 text-lg font-bold text-[var(--ink)]">{c.title}</h3>
                <p className="font-serif-body mt-1 flex-1 text-sm text-slate-600">{c.description}</p>
                <p className="font-ui mt-3 flex items-center gap-1.5 text-xs text-slate-500"><Phone size={12} className="text-[var(--gold)]" /> {c.name} · {c.contact}</p>
              </div>
            </div>
          ))}
          {items.length === 0 && <p className="font-ui col-span-full py-16 text-center text-slate-500">No ads in this category yet. Be the first to post!</p>}
        </div>
      </div>

      {open && (
        <Modal title="Place a Classified Ad" onClose={() => setOpen(false)} onSubmit={submit} busy={busy} submitLabel="Post Ad">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Category"><select data-testid="cl-category" value={form.category} onChange={set("category")} className={inputCls}>{CATS.filter((c) => c !== "All").map((c) => <option key={c}>{c}</option>)}</select></Field>
            <Field label="Price (optional)"><input data-testid="cl-price" value={form.price} onChange={set("price")} className={inputCls} placeholder="$0" /></Field>
            <Field label="Title" full><input data-testid="cl-title" value={form.title} onChange={set("title")} className={inputCls} /></Field>
            <Field label="Description" full><textarea data-testid="cl-description" value={form.description} onChange={set("description")} rows={3} className={inputCls} /></Field>
            <Field label="Photo" full><ImageUpload value={form.image} onChange={(url) => setForm((f) => ({ ...f, image: url }))} testid="cl-image" /></Field>
            <Field label="Your name"><input data-testid="cl-name" value={form.name} onChange={set("name")} className={inputCls} /></Field>
            <Field label="Contact (phone/email)"><input data-testid="cl-contact" value={form.contact} onChange={set("contact")} className={inputCls} /></Field>
          </div>
        </Modal>
      )}
    </div>
  );
}
